import {
	ReactElement,
	useCallback
} from "react";
import { useRegisterTileUsage } from "./useRegisterTileUsage";
import { localize } from "client_legacy/ndaw/culture";
import { navigateToLocalApp } from "client_legacy/Apps/appNavigation";
import { AlertManager, useAlerts } from "client/Hooks";
import { useRenderManager } from "../useRenderManager";
import { TwoByTwoTileContent } from "./TwoByTwoTileContent";
import { OneByOneTileContent } from "./OneByOneTileContent";
import { TileRenderState } from "../RenderState/common";
import { AppType } from "client/Api/Apps/common";
import {
	useBaseNavigation,
	useConditionalAccessNavigation,
	useMaintenanceWindowNavigation
} from "./useInvokeNavigation";
import { TileContextProvider } from "../TileContextProvider";
import { TimeZone } from "client/DateAndTime/timezones";
import { Coordinate } from "client/Misc/commonTypes";

export type LocalAppTileContentProps = {
	renderState: TileRenderState<AppType.LocalApp>;
	onEdit: (() => void) | undefined;
	userTimezone: TimeZone;
	onOpenTileMenu: (coordinate: Coordinate) => void;
};

export const LocalAppTileContent = (props: LocalAppTileContentProps): ReactElement => {
	const {
		renderState,
		onEdit,
		userTimezone,
		onOpenTileMenu
	} = props;

	const [navigationElement, navigateHandler] = useLocalAppTileNavigation(
		renderState,
		onEdit,
		userTimezone
	);

	const { dimension } = renderState;

	const tileContent: ReactElement = useRenderManager(
		dimension,
		() => (
			<TwoByTwoTileContent
				onClick={navigateHandler}
				onOpenTileMenu={onOpenTileMenu}
			/>
		),
		[
			{ columnSpan: 1, rowSpan: 1 },
			() => (
				<OneByOneTileContent
					onClick={navigateHandler}
					onOpenTileMenu={onOpenTileMenu}
				/>
			)
		]
	);

	return (
		<>
			<TileContextProvider
				renderState={renderState}
			>
				{tileContent}
			</TileContextProvider>

			{navigationElement}
		</>
	);
};
LocalAppTileContent.displayName = "LocalAppTileContent";

export function useLocalAppTileNavigation(
	renderStateIsh: Pick<TileRenderState<AppType.LocalApp>, "data" | "dimension" | "interactionState">,
	onEdit: (() => void) | undefined,
	userTimezone: TimeZone
): [element: ReactElement | null, () => void | Promise<void>] {
	const registerTileUsage: () => void = useRegisterTileUsage(renderStateIsh);
	const alertManager: AlertManager = useAlerts();

	const {
		environmentAppId,
		name,
		conditionalAccess,
		maintenanceWindow
	} = renderStateIsh.data;

	return useBaseNavigation(
		renderStateIsh,
		onEdit,
		useConditionalAccessNavigation(
			conditionalAccess,
			useMaintenanceWindowNavigation(
				maintenanceWindow,
				userTimezone,
				useCallback(
					(): void => {
						const message: string = localize("_OpeningTheAppTileNameOnYourLocalDevice")
							.replace("{{TileName}}", name);

						alertManager.showInfo(message);

						registerTileUsage();

						navigateToLocalApp({
							id: environmentAppId
						});
					},
					[
						alertManager,
						environmentAppId,
						name,
						registerTileUsage
					]
				)
			)
		)
	);
}
